import React, { useEffect, useMemo, useState, useContext } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Layout from "../layout/default";
import { Icon, Block } from "../components";
import { useApiClient } from "../ApiService/useApiClient";
import { carePlansApi } from "../ApiService/carePlansApi";
import { AppContext } from "../context/AppContext";

const safeArr = (v) => (Array.isArray(v) ? v : []);

const normalizePlan = (p) => {
  if (!p) return null;
  const id = p.id ?? p.Id;
  const customerId = p.customerId ?? p.CustomerId ?? p.customerid ?? p.CustomerID ?? null;
  const title = p.title ?? p.Title ?? "";
  const status = p.status ?? p.Status ?? "";
  const effectiveFrom = p.effectiveFrom ?? p.EffectiveFrom ?? null;
  const nextReviewOn = p.nextReviewOn ?? p.NextReviewOn ?? null;
  const version = p.version ?? p.Version ?? 1;
  const summary = p.summary ?? p.Summary ?? "";

  const sectionsRaw = p.sections ?? p.Sections ?? [];
  const sections = safeArr(sectionsRaw).map((s) => ({
    type: s.type ?? s.Type ?? "",
    content: s.content ?? s.Content ?? "",
    risks: safeArr(s.risks ?? s.Risks),
  }));

  return { id, customerId, title, status, effectiveFrom, nextReviewOn, version, summary, sections };
};

const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toISOString().slice(0, 10);
};

const statusVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("active")) return "success";
  if (s.includes("pending")) return "warning";
  if (s.includes("draft")) return "secondary";
  if (s.includes("archiv")) return "dark";
  return "primary";
};

export default function CarePlanView() {
  const { planId } = useParams();
  const api = useApiClient();
  const apiWrap = useMemo(() => carePlansApi(api), [api]);

  const { SiteConfig } = useContext(AppContext);
  const isAdmin = SiteConfig?.CURRENT_USER_ROLE === "admin";

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiWrap.getById(planId);
        const normalized = normalizePlan(res.data);
        if (alive) setPlan(normalized);
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [apiWrap, planId]);

  if (loading) {
    return (
      <Layout title="Care Plan" content="container">
        <div className="py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Care Plan" content="container">
        <Alert variant="danger">
          Failed to load care plan: {String(error?.response?.data?.message || error?.message || error)}
        </Alert>
      </Layout>
    );
  }

  if (!plan) {
    return (
      <Layout title="Care Plan" content="container">
        <Alert variant="warning">Care plan not found.</Alert>
      </Layout>
    );
  }

  const overdue =
    plan.nextReviewOn && new Date(plan.nextReviewOn).getTime() < new Date().setHours(0, 0, 0, 0);

  return (
    <Layout title="Care Plan" content="container">
      <Block>
        <Card>
          <Card.Body>
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h4 className="mb-0">{plan.title || "Care Plan"}</h4>
                  <Badge bg={statusVariant(plan.status)}>{plan.status || "Unknown"}</Badge>
                  {overdue && <Badge bg="danger">Review overdue</Badge>}
                </div>

                <div className="small text-muted mt-1">
                  Version: <b>{plan.version ?? 1}</b> • Effective: <b>{fmtDate(plan.effectiveFrom)}</b> • Next review:{" "}
                  <b>{fmtDate(plan.nextReviewOn)}</b>
                </div>
              </div>

              <div className="d-flex gap-2">
                <Link to={`/care-plans/${plan.id}/print`} target="_blank" rel="noreferrer">
                  <Button variant="outline-secondary" size="sm">
                    <Icon name="printer" /> <span>Print</span>
                  </Button>
                </Link>

                {plan.customerId ? (
                  <Link to={`/client/${plan.customerId}`}>
                    <Button variant="outline-primary" size="sm">
                      <Icon name="user" /> <span>Customer</span>
                    </Button>
                  </Link>
                ) : null}

                {/* If you later add a standalone edit route, this link is ready */}
                {isAdmin ? (
                  <Link to={`/care-plans/${plan.id}/edit`}>
                    <Button variant="primary" size="sm">
                      <Icon name="edit" /> <span>Edit</span>
                    </Button>
                  </Link>
                ) : null}
              </div>
            </div>

            <hr />

            <Row className="g-3">
              <Col xs={12}>
                <div className="fw-semibold mb-2">Summary</div>
                <div className="p-3 rounded" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
                  {plan.summary ? (
                    <div>{plan.summary}</div>
                  ) : (
                    <div className="small text-muted">No summary</div>
                  )}
                </div>
              </Col>

              <Col xs={12}>
                <div className="fw-semibold mb-2">Sections</div>

                {plan.sections?.length ? (
                  <div className="d-flex flex-column gap-3">
                    {plan.sections.map((s) => (
                      <div key={s.type} className="p-3 rounded" style={{ border: "1px solid var(--border)" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="fw-semibold">{String(s.type).replace(/_/g, " ")}</div>
                          {s.risks?.length ? (
                            <div className="d-flex flex-wrap gap-2">
                              {s.risks.slice(0, 6).map((r) => (
                                <Badge key={r} bg="warning" text="dark">
                                  {r}
                                </Badge>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-2">
                          {s.content ? (
                            <div dangerouslySetInnerHTML={{ __html: s.content }} />
                          ) : (
                            <div className="small text-muted">No content</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="small text-muted">No sections found.</div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Block>
    </Layout>
  );
}
